import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../redux/hooks";
import { useSubmitWalmartAdvertiserId } from "./api/useSubmitWalmartAdvertiserId";
import { useWalmartAdvertisingSetup } from "./api/useWalmartAdvertisingSetup";
import { useWalmartOnboardingStatus } from "./useWalmartOnboardingStatus";
import { WalmartPartnerAccessInstructions } from "./WalmartPartnerAccessInstructions";
import type { AdvertiserVerificationError } from "./useAdvertiserVerificationError";
import { css } from "../../../../styled-system/css";
import { Center, VStack } from "../../../../styled-system/jsx";
import { TextField } from "../../../components/fields/base/TextField";
import { Button } from "../../../components/core/basic/Button";
import { SECONDARY_CHANNEL_STORAGE_KEY } from "../shared/constants";

interface WalmartAdvertisingConnectProps {
  onConnected: () => void;
  sellerType: string | undefined;
}

export const WalmartAdvertisingConnect = ({ onConnected, sellerType }: WalmartAdvertisingConnectProps) => {
  const [advertiserId, setAdvertiserId] = useState("");
  const [failure, setFailure] = useState<AdvertiserVerificationError | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useTranslation(["onboarding", "common"]);

  const amz_onboard = useAppSelector((state) => state.amz_onboard);
  const user = useAppSelector((state) => state.user);

  const { data: onboardingStatus } = useWalmartOnboardingStatus();
  const savedAdvertiserId = onboardingStatus?.advertiser_id ?? null;
  const partnerAccessState = onboardingStatus?.partner_access_state;

  const submitWalmartAdvertiserId = useSubmitWalmartAdvertiserId({
    onSuccess: () => {
      setFailure(null);
      setIsEditing(false);
      onConnected();
    },
    onError: setFailure,
  });

  const recheckAccess = useWalmartAdvertisingSetup();

  const submit = (force: boolean) => {
    submitWalmartAdvertiserId.mutate({
      advertiser_id: advertiserId,
      specified_org_name: user.organization_name,
      standalone: amz_onboard.org_status === "onboarding",
      profile_type: sellerType ?? "",
      onboarding: true,
      created_by: parseInt(user.user_id),
      secondary_channel_onboarding: localStorage[SECONDARY_CHANNEL_STORAGE_KEY] === "walmart",
      force,
    });
  };

  const startEditing = () => {
    setAdvertiserId(savedAdvertiserId ?? "");
    setFailure(null);
    setIsEditing(true);
  };

  if (savedAdvertiserId && !isEditing && partnerAccessState === "connected") {
    return (
      <VStack gap="medium" alignItems="stretch" paddingY="medium">
        <span className={css({ textAlign: "center" })}>
          {t(($) => $.onboarding["Trellis is connected to advertiser ID {advertiserId}."], {
            advertiserId: savedAdvertiserId,
          })}
        </span>
        <Center>
          <Button variant="link" onPress={startEditing}>
            {t(($) => $.onboarding["Use a different advertiser ID"])}
          </Button>
        </Center>
      </VStack>
    );
  }

  if (savedAdvertiserId && !isEditing && partnerAccessState === "pending") {
    return (
      <VStack gap="medium" alignItems="stretch" paddingY="medium">
        <span className={css({ textAlign: "center" })}>
          {t(($) => $.onboarding["Advertiser ID {advertiserId} is saved, but Walmart has not confirmed access yet."], {
            advertiserId: savedAdvertiserId,
          })}
        </span>
        <Center>
          <Button
            variant="primary"
            colorScheme="fuchsia"
            isPending={recheckAccess.isPending}
            onPress={() => recheckAccess.mutate({ advertiserId: savedAdvertiserId, reauth: true })}
          >
            {t(($) => $.onboarding["Check access now"])}
          </Button>
        </Center>
        <Center>
          <Button variant="link" onPress={startEditing}>
            {t(($) => $.onboarding["Use a different advertiser ID"])}
          </Button>
        </Center>
      </VStack>
    );
  }

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      className={css({ display: "flex", gap: "large", flexDirection: "column", width: "100%" })}
    >
      <VStack gap="large" alignItems="stretch">
        <WalmartPartnerAccessInstructions />

        <TextField
          type="text"
          name="advertiserId"
          value={advertiserId}
          label={t(($) => $.onboarding["Advertiser ID"])}
          description={t(
            ($) =>
              $.onboarding[
                "On your Ad Center home screen, choose a Partner, Organization and Account — the Advertiser ID is the number under the account name."
              ],
          )}
          errorMessage={failure?.message}
          onChange={(value) => {
            setAdvertiserId(value);
            setFailure(null);
          }}
        />

        <Center>
          <Button
            onPress={() => submit(false)}
            variant="primary"
            isDisabled={!advertiserId}
            isPending={submitWalmartAdvertiserId.isPending}
            width="50%"
            colorScheme="fuchsia"
          >
            {t(($) => $.onboarding["Verify & Connect"])}
          </Button>
        </Center>

        {failure?.retryable && (
          <Center>
            <Button variant="link" onPress={() => submit(true)} isPending={submitWalmartAdvertiserId.isPending}>
              {t(($) => $.onboarding["I have already added Trellis — save this ID and continue"])}
            </Button>
          </Center>
        )}
      </VStack>
    </form>
  );
};
